/**
 * Inverse-parse branch for the value-list shapes the filter emitters write
 * (`inListSql`, `inListFilterSql.ts`) — the "in list" / "not in list"
 * operators and the `enum` multi-select:
 *
 *   `<field> IN (<v>, …)` / `<field> NOT IN (<v>, …)`
 *
 * and, on Oracle, a list past its 1,000-value limit, written as 1,000-value
 * lists in one pair of parentheses: `(<field> IN (…) OR <field> IN (…))` /
 * `(<field> NOT IN (…) AND <field> NOT IN (…))`. Only that exact shape reads
 * back as one list (every part but the last holds 1,000 values), so
 * re-applying it writes the same text.
 *
 * Each value is read back as the emitter writes it (`parseComparisonLiteral`):
 * a text literal (on MySQL also the `_utf8mb4 X'<hex>'` form of a value with
 * `\` or NUL), a number, or a temporal literal (Oracle `TO_DATE(…)`, …). Any
 * other element — `NULL`, a column, a function call, a subquery — means the
 * list is not one a chip wrote: `null`, and the predicate stays raw text. A
 * list of temporal literals carries the column type they were written for
 * (`valueDataType`, `temporalTypeOfLiterals`), so it re-applies as written on
 * a column the schema does not know.
 *
 * The result is always the list operator (`IN` / `NOT IN`): the text is the
 * same for an `enum` multi-pick, and the column-aware reopen step
 * (`upgradeReopenedCondition`) turns it into `enum` on a column whose chip
 * defaults to the enum picker.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { FilterCondition } from '../../core/types/workspaceTypes';
import { ORACLE_IN_LIST_LIMIT } from './inListFilterSql';
import { QUALIFIED_IDENT, QUALIFIED_IDENT_SRC } from './predicateIdentGrammar';
import { splitTopLevelCommaList } from './predicateJsonHelpers';
import {
  parseComparisonLiteral,
  temporalTypeOfLiterals,
  topLevelKeywords,
  unwrapParentheses,
} from './predicateScalarShapes';

type Parsed = { field: string; condition: FilterCondition; valueDataType?: string };
type ListPart = {
  readonly field: string;
  readonly negated: boolean;
  readonly values: (string | number)[];
  /** The literals as written — their temporal type is the list's (`temporalTypeOfLiterals`). */
  readonly raws: readonly string[];
};

const IN_LIST_RE = new RegExp(`^(${QUALIFIED_IDENT_SRC})\\s+(NOT\\s+)?IN\\s*\\(([\\s\\S]+)\\)$`, 'i');

function parseOneList(s: string, engine: DatabaseEngineId | undefined): ListPart | null {
  const m = s.trim().match(IN_LIST_RE);
  if (!m) return null;
  const field = m[1]!.trim();
  if (!QUALIFIED_IDENT.test(field)) return null;
  const parts = splitTopLevelCommaList(m[3]!);
  if (!parts || parts.length === 0) return null;
  const values: (string | number)[] = [];
  for (const raw of parts) {
    const value = parseComparisonLiteral(raw, engine);
    if (value === null) return null;
    values.push(value);
  }
  return { field, negated: !!m[2], values, raws: parts };
}

function toParsed({ field, negated, values, raws }: ListPart): Parsed {
  const valueDataType = temporalTypeOfLiterals(raws);
  return { field, condition: { operator: negated ? 'NOT IN' : 'IN', value: values }, ...(valueDataType ? { valueDataType } : {}) };
}

/** Oracle's split list: `(f IN (…1,000…) OR f IN (…))`, or the `NOT IN` / `AND` form. */
function parseOracleSplitList(s: string, engine: DatabaseEngineId): Parsed | null {
  const inner = unwrapParentheses(s);
  if (inner === null) return null;
  for (const keyword of ['OR', 'AND'] as const) {
    const offsets = topLevelKeywords(inner, keyword);
    if (!offsets || offsets.length === 0) continue;
    const pieces = [...offsets, inner.length].map((end, i) =>
      inner.slice(i === 0 ? 0 : offsets[i - 1]! + keyword.length, end),
    );
    const lists = pieces.map((piece) => parseOneList(piece, engine));
    const negated = keyword === 'AND';
    const first = lists[0];
    if (!first || lists.some((l) => !l || l.field !== first.field || l.negated !== negated)) return null;
    const sizes = lists.map((l) => l!.values.length);
    const last = sizes.pop()!;
    if (sizes.some((n) => n !== ORACLE_IN_LIST_LIMIT) || last > ORACLE_IN_LIST_LIMIT) return null;
    return toParsed({
      field: first.field,
      negated,
      values: lists.flatMap((l) => l!.values),
      raws: lists.flatMap((l) => l!.raws),
    });
  }
  return null;
}

export function parseInListPredicate(s: string, engine?: DatabaseEngineId): Parsed | null {
  const single = parseOneList(s, engine);
  if (single) return toParsed(single);
  return engine === 'oracle' ? parseOracleSplitList(s, engine) : null;
}
